"use client";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@restomat/components/ui";
import { Button } from "@restomat/components/client/ui";
import { Check } from "lucide-react";
import { getPrettyPrice } from "@restomat/shared-utils/utils";
import { cn } from "@restomat/shared-utils/utils";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { useTranslations } from "next-intl";
import { posterUnroll } from "./animations";

/**
 * A single tier card. Tiers are nested, so there is nothing to be incompatible with — picking one
 * simply replaces the previous choice, and every card is always selectable.
 */
export default function PricelistCard({
  tierId,
  title,
  description,
  features,
  price,
  isFree = false,
  highlightBadge,
  selected = false,
  onClick = undefined,
  className = "",
}: {
  tierId: string;
  title: string;
  description: string;
  features: string[];
  price: number;
  isFree?: boolean;
  highlightBadge?: string;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  const t = useTranslations("landing.pricing");
  const prettyPrice = getPrettyPrice(price);
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, amount: "some" });

  return (
    <motion.div
      ref={cardRef}
      variants={posterUnroll}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      <div
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        data-testid={`pricing-tier-${tierId}`}
        data-selected={selected}
        className={cn(
          "h-full cursor-pointer transition-all duration-200",
          selected ? "" : "hover:scale-[1.02]",
        )}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.();
          }
        }}
      >
        <Card
          className="h-full"
          contentClassName={"justify-between"}
          variant={selected ? "default" : "light"}
        >
          {/* Badge lives inside the header rather than floating over the card edge: the title is
              set in a tall display face whose diacritics reach well above its box, so an
              overlapping pill collides with them. */}
          <CardHeader className={"flex flex-col gap-2"}>
            {highlightBadge && (
              <span className="w-max rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wide whitespace-nowrap text-white">
                {highlightBadge}
              </span>
            )}
            <div className="flex w-full flex-row gap-2 justify-between items-start">
              <h3
                className={
                  "font-primary font-stretch-expanded text-leading-md-black"
                }
              >
                {title}
              </h3>
              <Button
                size={"icon"}
                variant={selected ? "default" : "light"}
                className={cn(selected ? "" : "text-muted-foreground")}
                aria-label={
                  selected
                    ? t("selectedPackage", { title })
                    : t("selectPackage", { title })
                }
              >
                <Check className={cn(selected ? "opacity-100" : "opacity-0")} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className={"text-left"}>{description}</p>

            <ul className="mt-4 flex flex-col gap-2 text-left">
              {features.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <Check className="mt-1 size-4 shrink-0 text-primary" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>

          <CardFooter className="flex-col items-start gap-2">
            <div
              className={
                "font-headings font-black font-stretch-ultra-condensed text-4xl md:text-7xl"
              }
            >
              {isFree ? (
                t("free")
              ) : (
                <>
                  {prettyPrice} {t("currency")}{" "}
                  <span className={"text-lg font-medium"}>
                    {t("perMonth")}
                  </span>
                </>
              )}
            </div>
          </CardFooter>
        </Card>
      </div>
    </motion.div>
  );
}
